export function bestPriceFromUpdate (update, quantity, exchangeRate = 1, discount = null) {
  let quantityToOrder = minimumQuantityToOrder(update, quantity)

  const prices = !discount ? update.prices : applyDiscount(update.prices, discount)

  let bestLinePrice = null
  prices.forEach(priceItem => {
    if (quantityToOrder > priceItem.quantity) {
      if (bestLinePrice === null || (quantityToOrder * priceItem.price + update.additionnalFee) < bestLinePrice) {
        bestLinePrice = quantityToOrder * priceItem.price + update.additionnalFee
      }
    } else {
      if (bestLinePrice === null || (priceItem.quantity * priceItem.price + update.additionnalFee) < bestLinePrice) {
        bestLinePrice = priceItem.quantity * priceItem.price + update.additionnalFee
        quantityToOrder = priceItem.quantity
      }
    }
  })

  return {
    quantityToOrder,
    linePrice: bestLinePrice,
    linePriceNormalized: bestLinePrice * exchangeRate,
    unitPrice: quantityToOrder && bestLinePrice ? bestLinePrice / quantityToOrder : null,
    unitPriceNormalized: quantityToOrder && bestLinePrice ? bestLinePrice / quantityToOrder * exchangeRate : null,
    excessQuantity: quantityToOrder && bestLinePrice ? (quantityToOrder - quantity) : null,
    excessCost: quantityToOrder && bestLinePrice ? (quantityToOrder - quantity) * bestLinePrice / quantityToOrder : null,
    excessCostNormalized: quantityToOrder && bestLinePrice ? (quantityToOrder - quantity) * bestLinePrice / quantityToOrder * exchangeRate : null,
    currency: update.currency
  }
}

function minimumQuantityToOrder (update, quantity) {
  let minimumQuantityToOrder = null

  if (quantity < update.minimumOrderQuantity) {
    minimumQuantityToOrder = Math.ceil(update.minimumOrderQuantity / update.multiplePackagingQuantity) * update.multiplePackagingQuantity
  } else {
    minimumQuantityToOrder = Math.ceil(quantity / update.multiplePackagingQuantity) * update.multiplePackagingQuantity
  }

  return minimumQuantityToOrder
}

function applyDiscount (prices, { type, value }) {
  if (value < 0 || value > 100) {
    return prices
  }

  switch (type) {
    case 'flatDiscount':
      return prices.map(item => ({
        ...item,
        price: item.price * (1 - value / 100)
      }))
    case 'columnJump':
      return prices.map((item, index, pricesArray) => ({
        ...item,
        price: (index + value) < (pricesArray.length - 1) ? pricesArray[index + value].price : pricesArray[pricesArray.length - 1].price
      }))
    case 'columnMin':
      return prices.map((item, index, pricesArray) => {
        const response = { ...item }

        if (value >= pricesArray.length) {
          response.price = pricesArray[pricesArray.length - 1].price
        } else if (index < (value - 1)) {
          response.price = pricesArray[value - 1].price
        }

        return response
      })
    default:
      return prices
  }
}

function availableAtDate (update, quantity, dateExpected) {
  // If today the stock is enought, return true
  if (update.stock >= quantity) {
    return true
  }

  // If date expected is higher than a valid leadTime, return true
  const today = new Date()
  const leadTime = today.setDate(today.getDate() + update.leadTime)
  if (update.leadTime && dateExpected > leadTime) {
    return true
  }

  return false
}

export function checkPartNumberMatching (partNumber, query) {
  if (query) {
    query = query.trim().toUpperCase()
  }
  if (partNumber) {
    partNumber = partNumber.trim().toUpperCase()
  }

  if (partNumber === query) {
    return { match: 'exact', matchPriority: 8 }
  }
  if (partNumber.startsWith(query)) {
    return { match: 'start', matchPriority: 6 }
  }
  if (partNumber.includes(query, 1)) {
    return { match: 'contain', matchPriority: 4 }
  }

  return { match: 'no', matchPriority: 2 }
}

export function bestOptionFromArticleList (articles, quantity, dateExpected = null, suppliersDiscount = []) {
  let bestOption = null

  articles.forEach((article) => {
    // Ignore if we need at a specific date and can't get stock for it and lead time doesn't match
    if (dateExpected && !availableAtDate(article.update, quantity, dateExpected)) {
      return null
    }

    // Calculate the price with an article and store it if total (need + excess) are cheaper than previous option
    const articlePrice = bestPriceFromUpdate(article.update, quantity, article.supplier.exchangeRate, suppliersDiscount[article.supplier.id])
    if (
      articlePrice.linePrice > 0 &&
      (bestOption === null || articlePrice.linePriceNormalized < bestOption.linePriceNormalized)
    ) {
      bestOption = { ...articlePrice, ...{ article } }
    }
  })

  return bestOption
}

export function preferredOptionFromArticleList (articles, quantity, query = null, preferredSupplierIds = [], dateExpected = null, suppliersDiscount = []) {
  // Check if exact match with prefered suppliers
  let bestOption = bestOptionFromArticleList(
    articles.filter(article => preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'exact'),
    quantity,
    dateExpected,
    suppliersDiscount
  )

  // Check if exact match with other suppliers
  if (!bestOption) {
    bestOption = bestOptionFromArticleList(
      articles.filter(article => !preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'exact'),
      quantity,
      dateExpected,
      suppliersDiscount
    )
  }

  if (bestOption) {
    return { ...bestOption, ...{ match: 'exact', matchPriority: 8 } }
  }

  // Check if start match with prefered suppliers
  bestOption = bestOptionFromArticleList(
    articles.filter(article => preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'start'),
    quantity,
    dateExpected,
    suppliersDiscount
  )

  // Check if start match with other suppliers
  if (!bestOption) {
    bestOption = bestOptionFromArticleList(
      articles.filter(article => !preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'start'),
      quantity,
      dateExpected,
      suppliersDiscount
    )
  }

  if (bestOption) {
    return { ...bestOption, ...{ match: 'start', matchPriority: 6 } }
  }

  // Check if contain match with prefered suppliers
  bestOption = bestOptionFromArticleList(
    articles.filter(article => preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'contain'),
    quantity,
    dateExpected,
    suppliersDiscount
  )

  // Check if contain match with other suppliers
  if (!bestOption) {
    bestOption = bestOptionFromArticleList(
      articles.filter(article => !preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'contain'),
      quantity,
      dateExpected,
      suppliersDiscount
    )
  }

  if (bestOption) {
    return { ...bestOption, ...{ match: 'contain', matchPriority: 4 } }
  }

  // Check other articles from the prefered suppliers
  bestOption = bestOptionFromArticleList(
    articles.filter(article => preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'no'),
    quantity,
    dateExpected,
    suppliersDiscount
  )

  // Check other articles from the other suppliers
  if (!bestOption) {
    bestOption = bestOptionFromArticleList(
      articles.filter(article => !preferredSupplierIds.includes(article.supplier.id) && checkPartNumberMatching(article.manufacturerPartNumber, query).match === 'no'),
      quantity,
      dateExpected,
      suppliersDiscount
    )
  }

  if (bestOption) {
    return { ...bestOption, ...{ match: 'no', matchPriority: 2 } }
  }

  return null
}
